// Hooks
import { useMemo, useState, useEffect, startTransition } from "react";
import { useParams, useLocation } from "react-router";

/** Stable URL/route params (patientId, encounterId, etc.) that don't fluctuate in modal context. @agentic connections */
export type UseStableParamsResult = {
  patientId: string | undefined;
  appointmentId: string | undefined;
  encounterId: string | undefined;
  organizationId: string | undefined;
  practitionerId: string | undefined;
  taskId: string | undefined;
  healthcareServiceId: string | undefined;
  clinicalImpressionId: string | undefined;
  coverageId: string | undefined;
  observationId: string | undefined;
};

/**
 * Hook that provides stable parameter values that don't fluctuate in modal context.
 * @agentic connections
 */
export function useStableParams(): UseStableParamsResult {
  const params = useParams();
  const location = useLocation();

  // Use state to cache stable values once we get valid ones
  // This avoids ref access during render
  const [stableValues, setStableValues] = useState<
    Partial<UseStableParamsResult>
  >({});

  // Compute current values from params and URL
  const currentValues = useMemo(() => {
    const getParam = (
      paramName: keyof UseStableParamsResult,
      urlPattern: RegExp,
    ) => {
      const rawValue = params[paramName];
      const urlMatch = location.pathname.match(urlPattern);
      const urlValue = urlMatch?.[1] !== "new" ? urlMatch?.[1] : undefined;
      return urlValue || rawValue;
    };

    return {
      patientId: getParam("patientId", /\/Patient\/([^/]+)/),
      appointmentId: getParam("appointmentId", /\/Appointment\/([^/]+)/),
      encounterId: getParam("encounterId", /\/Encounter\/([^/]+)/),
      organizationId: getParam("organizationId", /\/Organization\/([^/]+)/),
      practitionerId: getParam("practitionerId", /\/Practitioner\/([^/]+)/),
      taskId: getParam("taskId", /\/Task\/([^/]+)/),
      healthcareServiceId: getParam(
        "healthcareServiceId",
        /\/HealthcareService\/([^/]+)/,
      ),
      clinicalImpressionId: getParam(
        "clinicalImpressionId",
        /\/ClinicalImpression\/([^/]+)/,
      ),
      coverageId: getParam("coverageId", /\/Coverage\/([^/]+)/),
      observationId: getParam("observationId", /\/Observation\/([^/]+)/),
    };
  }, [params, location.pathname]);

  // Update stable values when we get valid current values
  useEffect(() => {
    const updates: Partial<UseStableParamsResult> = {};
    let hasUpdates = false;

    Object.entries(currentValues).forEach(([key, value]) => {
      if (
        value &&
        value !== "undefined" &&
        value !== "null" &&
        value !== stableValues[key as keyof UseStableParamsResult]
      ) {
        updates[key as keyof UseStableParamsResult] = value;
        hasUpdates = true;
      }
    });

    if (hasUpdates) {
      startTransition(() => {
        setStableValues((prev) => ({ ...prev, ...updates }));
      });
    }
  }, [currentValues, stableValues]);

  // Return stable values when current is invalid, otherwise return current
  return useMemo(() => {
    const result: UseStableParamsResult = {} as UseStableParamsResult;
    Object.keys(currentValues).forEach((key) => {
      const currentValue = currentValues[key as keyof UseStableParamsResult];
      const stableValue = stableValues[key as keyof UseStableParamsResult];

      if (
        currentValue &&
        currentValue !== "undefined" &&
        currentValue !== "null"
      ) {
        result[key as keyof UseStableParamsResult] = currentValue;
      } else {
        result[key as keyof UseStableParamsResult] = stableValue;
      }
    });
    return result;
  }, [currentValues, stableValues]);
}
